/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Traits", description = "Hightouch Traits enable users of any technical background to create and sync computed user attributes to various business applications.", lastUpdatedDate = new Date(1733356800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Traits",
        description: "Hightouch Traits enable users of any technical background to create and sync computed user attributes to various business applications.",
        lastUpdatedDate: "Thu Dec 05 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Trait types",
          "depth": 0,
          "id": "trait-types"
        }, {
          "value": "Create a trait",
          "depth": 0,
          "id": "create-a-trait"
        }, {
          "value": "Create a trait template",
          "depth": 0,
          "id": "create-a-trait-template"
        }, {
          "value": "Create a trait from a template",
          "depth": 0,
          "id": "create-a-trait-from-a-template"
        }, {
          "value": "Create audiences with traits",
          "depth": 0,
          "id": "create-audiences-with-traits"
        }, {
          "value": "Using trait templates in the audience",
          "depth": 1,
          "id": "using-trait-templates-in-the-audience"
        }, {
          "value": "Using traits in the audience",
          "depth": 1,
          "id": "using-traits-in-the-audience"
        }, {
          "value": "Creating a custom trait within the audience",
          "depth": 1,
          "id": "creating-a-custom-trait-within-the-audience"
        }, {
          "value": "Enrich audiences with traits",
          "depth": 0,
          "id": "enrich-audiences-with-traits"
        }, {
          "value": "Event traits",
          "depth": 0,
          "id": "event-traits"
        }, {
          "value": "Custom SQL traits",
          "depth": 0,
          "id": "custom-sql-traits"
        }, {
          "value": "JSON aggregations",
          "depth": 1,
          "id": "json-aggregations"
        }, {
          "value": "CASE statements",
          "depth": 1,
          "id": "case-statements"
        }, {
          "value": "Formula traits",
          "depth": 0,
          "id": "formula-traits"
        }, {
          "value": "Trait value suggestions",
          "depth": 0,
          "id": "trait-value-suggestions"
        }, {
          "value": "Enabling suggestions",
          "depth": 1,
          "id": "enabling-suggestions"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      code: "code",
      strong: "strong",
      ul: "ul",
      li: "li",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      ol: "ol",
      img: "img",
      h3: "h3",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Traits are part of the Advanced Customer Studio, available as an add-on to\n", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier plans"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["When defining audiences, you sometimes need computed or calculated attributes rather than raw data.\nFor example, you may want to create an audience based on Lifetime Value (LTV) though you don't have this column in your ", _jsx(_components.code, {
          children: "Users"
        }), " table.\nCreating it entails summing the price of all purchases made by a particular user."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch ", _jsx(_components.strong, {
          children: "Traits"
        }), " enable users of any technical background to:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/customer-studio/traits#create-a-trait",
            children: "Create computed fields"
          })
        }), "\n", _jsxs(_components.li, {
          children: ["Use them to ", _jsx(_components.a, {
            href: "#create-audiences-with-traits",
            children: "define audiences"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#enrich-audiences-with-traits",
            children: "Sync"
          }), " them to business applications"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can use these computed fields—called traits—to supercharge personalization across marketing channels without having to rely on data teams."
      }), "\n", _jsx(_components.h2, {
        id: "trait-types",
        children: "Trait types"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Trait Type"
            }), _jsx(_components.th, {
              children: "Example Usage"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sum"
              })
            }), _jsx(_components.td, {
              children: "Sum of price of all purchases a user has made in their lifetime"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Count"
              })
            }), _jsx(_components.td, {
              children: "Count of songs listened to in the past two weeks"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Average"
              })
            }), _jsx(_components.td, {
              children: "Average session time of a user"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Most Frequent"
              })
            }), _jsx(_components.td, {
              children: "Most Frequent category of articles read"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "First"
              })
            }), _jsx(_components.td, {
              children: "First page a user visited"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Last"
              })
            }), _jsx(_components.td, {
              children: "Last product a user viewed"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#custom-sql-traits",
                children: _jsx(_components.strong, {
                  children: "Custom SQL"
                })
              })
            }), _jsx(_components.td, {
              children: "All SKUs a user added to their cart before abandoning it"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#formula-traits",
                children: _jsx(_components.strong, {
                  children: "Formula"
                })
              })
            }), _jsx(_components.td, {
              children: "Use the result of a string column to create a boolean flag"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Traits are defined on ", _jsx(_components.a, {
          href: "/customer-studio/schema#parent-models",
          children: _jsx(_components.strong, {
            children: "parent model"
          })
        }), ", but you can also create traits based on ", _jsx(_components.a, {
          href: "#event-traits",
          children: "events"
        }), " to capture information about customer actions in a customer funnel."]
      }), "\n", _jsx(_components.h2, {
        id: "create-a-trait",
        children: "Create a trait"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/traits",
            children: _jsx(_components.strong, {
              children: "Traits"
            })
          }), " page and click ", _jsx(_components.strong, {
            children: "Create"
          }), " -> ", _jsx(_components.strong, {
            children: "New Trait"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/create-trait.png",
          alt: "Creating traits in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsx(_components.li, {
          children: "Select a parent model."
        }), "\n", _jsx(_components.li, {
          children: "Select the calculation method."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/trait-method.png",
          alt: "Configure trait method in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.a, {
            href: "/customer-studio/schema#relationships",
            children: "related model"
          }), " from which you're aggregating column values."]
        }), "\n", _jsx(_components.li, {
          children: "[Optional] Apply any conditions to the calculation method."
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.a, {
            href: "#trait-types",
            children: "trait type"
          }), ", for example, ", _jsx(_components.code, {
            children: "SUM"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Add required configurations for the trait type. For ", _jsx(_components.code, {
            children: "SUM"
          }), ", for instance, you need to select which column to sum."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/trait-calculation.png",
          alt: "Configure trait calculation in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "9",
        children: ["\n", _jsxs(_components.li, {
          children: ["Give the trait a descriptive ", _jsx(_components.strong, {
            children: "Name"
          }), ", for example, \"Lifetime Value.\""]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/finalize-trait.png",
          alt: "Finalize trait in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The trait is now part of the parent model, and you can use it when ", _jsx(_components.a, {
          href: "#create-audiences-with-traits",
          children: "creating"
        }), " audiences.\nIt will automatically be available to sync for any audience that belongs to this parent model."]
      }), "\n", _jsx(_components.h2, {
        id: "create-a-trait-template",
        children: "Create a trait template"
      }), "\n", _jsxs(_components.p, {
        children: ["Alternatively, you can also create a ", _jsx(_components.strong, {
          children: "Trait Template"
        }), ". Unlike Traits, Trait Templates can't have any conditions applied to it. However, you can then build traits off the template and apply any conditions you desire.\nTrait Templates allow you to easily reuse your trait configuration without having to start from scratch each time."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/traits",
            children: _jsx(_components.strong, {
              children: "Traits"
            })
          }), " page and click ", _jsx(_components.strong, {
            children: "Create"
          }), " -> ", _jsx(_components.strong, {
            children: "New template"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Go through the trait creation steps just like you would for a regular trait."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you have created a template, it will be available to all audiences under the same parent model. You can use the template as a filter for ", _jsx(_components.a, {
          href: "#create-audiences-with-traits",
          children: "creating"
        }), " audiences\nor use it to ", _jsx(_components.a, {
          href: "#enrich-audiences-with-traits",
          children: "create trait enrichments"
        }), " for syncing data."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Updating a trait template will also update its underlying traits. For\ninstance, if you were to change the trait calculation type of the template\nfrom ", _jsx(_components.code, {
            children: "SUM"
          }), " to ", _jsx(_components.code, {
            children: "COUNT"
          }), ", all of its underlying traits will also use the ", _jsx(_components.code, {
            children: "COUNT"
          }), "\naggregation."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "create-a-trait-from-a-template",
        children: "Create a trait from a template"
      }), "\n", _jsx(_components.p, {
        children: "After you create a template, you can use it as a base to create traits."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/traits",
            children: _jsx(_components.strong, {
              children: "Traits"
            })
          }), " page and click ", _jsx(_components.strong, {
            children: "Create"
          }), " -> ", _jsx(_components.strong, {
            children: "Start with a template"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a template and click ", _jsx(_components.strong, {
            children: "Choose template"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/select-trait-template.png",
          alt: "Select trait template in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsx(_components.li, {
          children: "[Optional] Add any conditions to the trait."
        }), "\n", _jsxs(_components.li, {
          children: ["Give the trait a descriptive ", _jsx(_components.strong, {
            children: "Name"
          }), ", for example, \"Lifetime Value.\""]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/finalize-templated-trait.png",
          alt: "Finalize templated trait in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "create-audiences-with-traits",
        children: "Create audiences with traits"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've ", _jsx(_components.a, {
          href: "#create-a-trait",
          children: "created a trait"
        }), ", you can use it for filtering within your audiences. Traits filters are under ", _jsx(_components.strong, {
          children: "Custom traits"
        }), " in the filters dropdown."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/trait-filter-option.png",
          alt: "Trait filter option"
        })
      }), "\n", _jsx(_components.p, {
        children: "There are three kinds of traits you can use in your audience filter:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Trait templates"
        }), "\n", _jsx(_components.li, {
          children: "Traits"
        }), "\n", _jsx(_components.li, {
          children: "Custom traits"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "using-trait-templates-in-the-audience",
        children: "Using trait templates in the audience"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add filter"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Navigate to ", _jsx(_components.strong, {
            children: "Custom traits"
          }), " and select a trait template.\nFor example, if you created an LTV trait, you can use it to filter for \"All users that have spent more than $100.\""]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/filter-basic-trait.png",
          alt: "Using a trait to filter audiences"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["[Optional] You can also add filters to apply before Hightouch calculates trait values. For example, you can restrict the LTV calculation to purchases where the ", _jsx(_components.code, {
            children: "Brand"
          }), " was Nike directly in the audience builder."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/filter-trait-with-conditions.png",
          alt: "Filtering traits in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["[Optional] You can save the trait filter as a new ", _jsx(_components.a, {
            href: "#enrich-audiences-with-traits",
            children: "trait enrichment"
          }), ". To do so, click the actions button and \"Save trait enrichment\""]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "using-traits-in-the-audience",
        children: "Using traits in the audience"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add filter"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Navigate to ", _jsx(_components.strong, {
            children: "Custom traits"
          }), " and select a trait"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/trait-audience-filter.png",
          alt: "Trait to filter audiences"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["[Optional] You can edit the trait configuration by clicking ", _jsx(_components.strong, {
            children: "Edit"
          }), ". Updating these filters will not affect the original trait."]
        }), "\n", _jsx(_components.li, {
          children: "[Optional] If you edit the trait and want to save it as a new trait, click the actions button and \"Save trait.\""
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/save-new-trait.png",
          alt: "Save edited trait"
        })
      }), "\n", _jsx(_components.h3, {
        id: "creating-a-custom-trait-within-the-audience",
        children: "Creating a custom trait within the audience"
      }), "\n", _jsxs(_components.p, {
        children: ["Maybe you don't have a suitable trait or trait template to act as a filter. Or, maybe you just want to experiment with a trait filter without necessarily creating one for the parent model.\nYou can create a ", _jsx(_components.strong, {
          children: "custom trait"
        }), " inline within the audience query builder."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add filter"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Navigate to ", _jsx(_components.strong, {
            children: "Custom traits"
          }), " and click ", _jsx(_components.strong, {
            children: "Create a custom trait"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Select a related model"
        }), "\n", _jsx(_components.li, {
          children: "Select the configuration for your trait"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/custom-trait-calculation.png",
          alt: "Custom trait calculation to filter audiences"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "5",
        children: ["\n", _jsx(_components.li, {
          children: "[Optional] If you decide that this custom trait is useful enough to persist it for the entire parent model, click the actions button and \"Save trait.\"\nDoing so converts it into a new trait that can be synced."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/save-custom-trait-calculation.png",
          alt: "Save custom trait calculation"
        })
      }), "\n", _jsx(_components.h2, {
        id: "enrich-audiences-with-traits",
        children: "Enrich audiences with traits"
      }), "\n", _jsxs(_components.p, {
        children: ["Traits defined on the parent model are automatically available for syncing to destinations. Alternatively, you can create ", _jsx(_components.strong, {
          children: "trait enrichments"
        }), " which are traits that are specific to an audience."]
      }), "\n", _jsx(_components.p, {
        children: "Trait enrichments are based on a parent trait which are the trait templates you've defined on the parent model."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/add-trait-enrichments.png",
          alt: "Adding a trait enrichment"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.strong, {
            children: "Traits"
          }), " tab of the audience you want to add enrichments to."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Create trait"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a ", _jsx(_components.strong, {
            children: "Name"
          }), " and select the ", _jsx(_components.strong, {
            children: "Parent trait"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "[Optional] Add conditions to apply on top of the parent trait."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/trait-enrichment-definition.png",
          alt: "Defining a trait enrichment"
        })
      }), "\n", _jsx(_components.p, {
        children: "Once created, these enrichment traits appear in the Audience preview and sync configuration like any other field."
      }), "\n", _jsx(_components.h2, {
        id: "event-traits",
        children: "Event traits"
      }), "\n", _jsxs(_components.p, {
        children: ["In addition to defining traits on parent models, you can also apply traits to events in the visual audience builder. Event traits automatically become ", _jsx(_components.a, {
          href: "#enrich-audiences-with-traits",
          children: "trait enrichments"
        }), ", meaning you can view them in the preview and sync their values. One common use case for event traits is to capture information about abandoned carts."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["To create a new event trait, click ", _jsx(_components.strong, {
            children: "Add trait"
          }), " on an event condition in the audience builder."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You can only access the ", _jsx(_components.strong, {
            children: "Add trait"
          }), " option from an event filter once you've\nsaved the audience."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/add-funnel-trait.png",
          alt: "Adding an event trait in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Give the event trait a descriptive ", _jsx(_components.strong, {
            children: "Name"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the aggregation you want to use, for example, ", _jsx(_components.strong, {
            children: "Last"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the information you want to capture about the event. For example, you may want to capture the ", _jsx(_components.code, {
            children: "Product_ID"
          }), ", ", _jsx(_components.code, {
            children: "SKU"
          }), ", or ", _jsx(_components.code, {
            children: "Brand"
          }), " of the final product a user added to their cart before abandoning it."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/funnel-trait.png",
          alt: "Defining an event trait in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "custom-sql-traits",
        children: "Custom SQL traits"
      }), "\n", _jsxs(_components.p, {
        children: ["Custom SQL traits provide a powerful escape hatch for custom aggregations that the ", _jsx(_components.a, {
          href: "#trait-types",
          children: "default aggregations"
        }), " don't cover. To build a SQL trait, ", _jsx(_components.a, {
          href: "#create-a-trait",
          children: "create a trait"
        }), " and follow these additional instructions:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "SQL"
          }), " as the ", _jsx(_components.strong, {
            children: "Aggregation"
          }), " type."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a raw SQL aggregation. For example, to create a ", _jsx(_components.code, {
            children: "SUM"
          }), " trait with just SQL, you would enter ", _jsx(_components.code, {
            children: "SUM({{ column \"price\" }})"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(Info, {
        children: [" ", _jsx(_components.strong, {
          children: "Column references"
        }), " must use the following syntax ", _jsx(_components.code, {
          children: "{{column \"<COLUMN_NAME>\"}}"
        }), ". Column names are case-sensitive. "]
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsx(_components.li, {
          children: "Enter a default value for when the SQL returns no rows."
        }), "\n", _jsxs(_components.li, {
          children: ["Define the ", _jsx(_components.strong, {
            children: "Property type"
          }), " of the SQL aggregation result."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "json-aggregations",
        children: "JSON aggregations"
      }), "\n", _jsx(_components.p, {
        children: "JSON aggregations are a helpful way to aggregate the raw data in a related model. For example, imagine you want to get a list of all product IDs users have purchased. You can do this with the following SQL trait:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-built_in",
            children: "ARRAY_AGG"
          }), "(", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "DISTINCT"
          }), " {{", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "column"
          }), " \"product_id\"}})\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "case-statements",
        children: "CASE statements"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "CASE"
        }), " statements are a useful way to create friendly options for aggregations. For example, imagine you want to bucket customers into ", _jsx(_components.code, {
          children: "low"
        }), ", ", _jsx(_components.code, {
          children: "medium"
        }), ", and ", _jsx(_components.code, {
          children: "high"
        }), " LTV. You can do this with the following SQL trait:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "CASE"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHEN"
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "SUM"
          }), "( {{", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "column"
          }), " \"price\"}} ) ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "100"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "THEN"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'high'"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHEN"
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "SUM"
          }), "( {{", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "column"
          }), " \"price\"}} ) ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "50"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "THEN"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'medium'"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ELSE"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'low'"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "END"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "formula-traits",
        children: "Formula traits"
      }), "\n", _jsx(_components.p, {
        children: "Formula traits allow you to reference parent model columns, merged columns, and other traits to build inline transformations. Unlike Custom SQL Traits, there is no aggregation against a single related model. Instead, you can reference columns from a multitude of sources.\nThis unlocks a powerful and flexible way to compose columns all within Hightouch."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/formula-trait-editor.gif",
          alt: "Formula trait editor"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Formula"
          }), " as the ", _jsx(_components.strong, {
            children: "Aggregation"
          }), " type."]
        }), "\n", _jsx(_components.li, {
          children: "Enter raw SQL. To reference a column or trait, type the name for the autocompletion dropdown to show up. Then, select it to populate the editor as shown above.\nHere are a few examples of SQL queries that can be done using formula traits:"
        }), "\n"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Combine multiple columns into one: ", _jsx(_components.code, {
            children: "CONCAT({{\"first_name\"}}, ' ', {{\"last_name\"}})"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Use a string column to create a boolean flag: ", _jsx(_components.code, {
            children: "CASE WHEN {{\"merged.pets.animal\"}} LIKE 'dog' THEN true ELSE false END"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Reference other traits: ", _jsx(_components.code, {
            children: "CASE WHEN {{\"trait.lifetime_total_value\"}} > 1000 THEN 'high' ELSE 'low' END"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(Info, {
        children: [_jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Column references"
          }), " in formula traits use special syntax. Please use the SQL editor's autocompletion feature to write the column references."]
        }), _jsxs(_components.p, {
          children: ["References to non-existent columns or traits will be shown with a red pill.\n", _jsx(_components.img, {
            src: "hightouch-audiences/traits/broken-formula-trait-column.png",
            alt: "Broken formula trait column"
          })]
        })]
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Define the ", _jsx(_components.strong, {
            children: "Property type"
          }), " of the SQL aggregation result."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "trait-value-suggestions",
        children: "Trait value suggestions"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Most Frequent"
        }), ", ", _jsx(_components.strong, {
          children: "First"
        }), ", ", _jsx(_components.strong, {
          children: "Last"
        }), ", ", _jsx(_components.a, {
          href: "#custom-sql-traits",
          children: _jsx(_components.strong, {
            children: "Custom SQL"
          })
        }), ", and ", _jsx(_components.a, {
          href: "#formula-traits",
          children: _jsx(_components.strong, {
            children: "Formula"
          })
        }), " traits that result in a string type support trait value suggestions. When enabled this provides ", _jsx(_components.strong, {
          children: "suggested values"
        }), " for the visual audience builder. For example, if you're creating an audience you want to filter on a \"Most Recent City\" trait, turning on suggestions populates a dropdown with values—San Francisco, Los Angeles, etc.—found in the dataset."]
      }), "\n", _jsx(_components.h3, {
        id: "enabling-suggestions",
        children: "Enabling suggestions"
      }), "\n", _jsx(_components.p, {
        children: "Trait value suggestions can be enabled when creating or viewing a trait."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/traits/trait-value-suggestions.png",
          alt: "Enabling trait value suggestions"
        })
      })]
    });
  }
}
export default MDXContent;
